import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Check } from 'lucide-react';
import { Reveal, FadeIn } from '@/components/motion';
import { PillLink, SectionHead } from '@/components/ui-bits';
import { LogoMark, OrnamentHeart, OrnamentStar, OrnamentLeaf } from '@/components/Ornaments';
import { ROUTE_SLUGS } from '@/i18n';
import { IMAGES } from '@/lib/assets';
import { getWithRetry } from '@/lib/api';

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const heroRef = useRef(null);
  const [stories, setStories] = useState([]);
  const [posts, setPosts] = useState([]);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const ornY2 = useTransform(scrollYProgress, [0, 1], [0, 160]);

  useEffect(() => {
    getWithRetry(`/stories?lang=${lang}`).then((r) => setStories(r.data)).catch(() => {});
    getWithRetry(`/blog?lang=${lang}`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, [lang]);

  const p = (key) => { const s = ROUTE_SLUGS[key][lang]; return s ? `/${lang}/${s}` : `/${lang}`; };
  const valuesRaw = t('home.solutionValues', { returnObjects: true });
  const values = Array.isArray(valuesRaw) ? valuesRaw : [];
  const helpRaw = t('home.helpItems', { returnObjects: true });
  const helpItems = Array.isArray(helpRaw) ? helpRaw : [];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden px-5 md:px-10 pt-10 md:pt-16 pb-8">
        <div className="max-w-[1280px] mx-auto grid lg:grid-cols-[1.08fr_0.92fr] gap-10 lg:gap-16 items-center">
          <div className="relative z-10">
            <FadeIn>
              <img src="/logo-full.png" alt="In Buona Compagnia — Relazioni che restano" data-testid="hero-logo-full"
                className="w-full max-w-[240px] sm:max-w-[280px] h-auto mb-6 -ml-1" />
            </FadeIn>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-[3.7rem] leading-[1.03] text-textDark">
              {t('home.hero.title')}
            </h1>
            <Reveal delay={0.15} className="mt-6 max-w-xl">
              <p className="font-serif italic text-xl md:text-2xl text-terracotta leading-snug">{t('home.hero.subtitle')}</p>
            </Reveal>
            <Reveal delay={0.24} className="mt-4 max-w-xl">
              <p className="text-lg md:text-xl text-textDark/70 leading-relaxed">{t('home.hero.desc')}</p>
            </Reveal>
            <Reveal delay={0.34} className="mt-8 flex flex-wrap gap-4">
              <PillLink to={p('contact')} testId="hero-cta-primary">{t('home.hero.ctaPrimary')} <ArrowRight size={20} /></PillLink>
              <PillLink to={p('how')} variant="outline" testId="hero-cta-secondary">{t('home.hero.ctaSecondary')}</PillLink>
            </Reveal>
          </div>

          <motion.div style={{ y: imgY }} className="relative z-0">
            <div className="relative rounded-[2.5rem] overflow-hidden border-[10px] border-rosa shadow-xl shadow-terracotta/10 rotate-[1.2deg]">
              <img src={IMAGES.hero} alt="" data-testid="hero-illustration" className="w-full h-[380px] md:h-[520px] object-cover" />
            </div>
            <motion.div style={{ y: ornY2 }} className="pointer-events-none absolute -bottom-8 left-2 text-salvia w-28 md:w-36 md:-left-6">
              <OrnamentHeart className="w-full h-auto" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 border-y border-terracotta/20 bg-rosa/60">
        <Marquee speed={16} gradient={false} autoFill>
          {[0, 1, 2, 3].map((k) => (
            <span key={k} className="flex items-center font-serif italic text-3xl md:text-4xl text-textDark/85 mx-6">
              {t('home.marquee')}
              <span className="text-terracotta mx-6 inline-flex"><OrnamentStar className="w-7 h-7" /></span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* PROBLEM */}
      <section data-testid="home-problem" className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[880px] mx-auto text-center">
          <Reveal>
            <span className="inline-block mb-5 text-sm font-semibold tracking-[0.18em] uppercase text-terracotta">{t('home.problemEyebrow')}</span>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] text-textDark">{t('home.problemTitle')}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-8 text-xl text-textDark/75 leading-relaxed">{t('home.problemP1')}</p></Reveal>
          <Reveal delay={0.16}><p className="mt-5 font-serif text-2xl md:text-3xl text-textDark leading-snug">{t('home.problemP2')}</p></Reveal>
          <Reveal delay={0.22}><p className="mt-5 text-xl text-textDark/75 leading-relaxed">{t('home.problemP3')}</p></Reveal>
        </div>
      </section>

      {/* SOLUTION */}
      <section data-testid="home-solution" className="bg-rosa px-5 md:px-10 py-20 md:py-28 relative overflow-hidden">
        <OrnamentLeaf className="absolute -right-10 bottom-0 w-80 text-terracotta/15 hidden md:block" />
        <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <SectionHead eyebrow={t('home.solutionEyebrow')} title={t('home.solutionTitle')} />
          <Reveal delay={0.1}>
            <p className="text-xl md:text-2xl text-textDark/85 leading-relaxed">{t('home.solutionText')}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {values.map((v, i) => (
                <span key={i} className="rounded-full bg-salvia/25 text-textDark px-4 py-2 text-base font-medium">{v}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* NON SOSTITUIAMO LA FAMIGLIA */}
      <section data-testid="home-family" className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1080px] mx-auto rounded-[2.5rem] bg-salvia/20 border border-salvia/40 px-8 md:px-16 py-14 md:py-20 text-center relative overflow-hidden">
          <OrnamentHeart className="absolute -top-4 right-8 w-24 text-salvia/40 hidden md:block" />
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight text-textDark">{t('home.familyTitle')}</h2>
            <p className="mt-6 text-xl md:text-2xl text-textDark/80 leading-relaxed max-w-3xl mx-auto">{t('home.familyText')}</p>
          </Reveal>
        </div>
      </section>

      {/* LONELINESS */}
      <section data-testid="home-loneliness" className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-[2.5rem] overflow-hidden border-[10px] border-rosa rotate-[-1.5deg]">
              <img src={IMAGES.story3} alt="" className="w-full h-[340px] md:h-[420px] object-cover" />
            </div>
          </Reveal>
          <SectionHead eyebrow={t('home.lonelinessEyebrow')} title={t('home.lonelinessTitle')} subtitle={t('home.lonelinessText')} />
        </div>
      </section>

      {/* CONTINUITY — dark editorial statement */}
      <section data-testid="home-continuity" className="bg-textDark text-beige px-5 md:px-10 py-24 md:py-32 relative overflow-hidden">
        <OrnamentHeart className="absolute -bottom-8 -left-6 w-40 text-terracotta/25" />
        <div className="max-w-[1000px] mx-auto text-center relative z-10">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">{t('home.continuityTitle')}</h2>
            <p className="mt-8 text-xl md:text-2xl text-beige/80 leading-relaxed max-w-3xl mx-auto">{t('home.continuityText')}</p>
          </Reveal>
        </div>
      </section>

      {/* STORIES */}
      <section className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <SectionHead eyebrow={t('home.storiesTitle')} title={t('home.storiesText')} />
            <PillLink to={p('stories')} variant="outline" testId="home-see-stories">{t('home.seeStories')} <ArrowRight size={20} /></PillLink>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {stories.map((s, i) => (
              <Reveal key={s.story_id} delay={i * 0.08}>
                <figure data-testid={`home-story-${i}`} className="bg-card rounded-3xl overflow-hidden border border-terracotta/10 h-full flex flex-col">
                  <img src={s.image} alt="" className="w-full h-52 object-cover" />
                  <figcaption className="p-7 flex flex-col gap-4 flex-1">
                    <p className="font-serif text-xl italic text-textDark/90 leading-snug">{s.quote}</p>
                    <div className="mt-auto">
                      <p className="font-semibold text-textDark">{s.name}</p>
                      <p className="text-sm text-salvia font-medium">{s.role}</p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE CAN BE THERE */}
      <section data-testid="home-help" className="bg-rosa/50 px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1080px] mx-auto text-center">
          <SectionHead align="center" eyebrow={t('home.helpText')} title={t('home.helpTitle')} />
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {helpItems.map((item, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <span className="inline-flex items-center gap-2.5 rounded-full bg-card border border-terracotta/15 px-5 py-3 text-lg text-textDark">
                  <Check size={18} className="text-terracotta" /> {item}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 flex justify-center"><PillLink to={p('help')} testId="home-help-cta">{t('home.helpCta')} <ArrowRight size={20} /></PillLink></div>
          </Reveal>
        </div>
      </section>

      {/* FOR FAMILIES */}
      <section data-testid="home-families" className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <SectionHead eyebrow={t('nav.collaborate')} title={t('home.forFamiliesTitle')} />
          <Reveal delay={0.1}>
            <p className="text-xl md:text-2xl text-textDark/80 leading-relaxed">{t('home.forFamiliesText')}</p>
            <div className="mt-8"><PillLink to={p('contact')} variant="sage" testId="home-families-cta">{t('home.forFamiliesCta')} <ArrowRight size={20} /></PillLink></div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="home-about" className="bg-rosa px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[900px] mx-auto flex flex-col md:flex-row items-center gap-10 text-center md:text-left">
          <span className="text-terracotta w-24 h-24 shrink-0"><LogoMark className="w-full h-full" /></span>
          <div>
            <SectionHead eyebrow={t('footer.tagline')} title={t('home.aboutTitle')} subtitle={t('home.aboutText')} />
            <div className="mt-7"><PillLink to={p('about')} variant="outline" testId="home-about-cta">{t('home.aboutCta')} <ArrowRight size={20} /></PillLink></div>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="px-5 md:px-10 py-20 md:py-28">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <SectionHead eyebrow={t('home.blogTitle')} title={t('home.blogText')} />
            <PillLink to={p('blog')} variant="outline" testId="home-see-blog">{t('nav.blog')} <ArrowRight size={20} /></PillLink>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {posts.map((post, i) => (
              <Reveal key={post.post_id} delay={i * 0.08}>
                <Link to={`/${lang}/blog/${post.slug}`} data-testid={`home-blog-${i}`} className="group block bg-card rounded-3xl overflow-hidden border border-terracotta/10 h-full">
                  <div className="overflow-hidden h-48">
                    <img src={post.cover_image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-xl text-textDark leading-snug mb-3 group-hover:text-terracotta transition-colors">{post.title}</h3>
                    <p className="text-textDark/70 line-clamp-3">{post.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 md:px-10 py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto rounded-[2.5rem] bg-terracotta text-beige px-8 md:px-16 py-16 md:py-24 text-center relative overflow-hidden">
          <OrnamentHeart className="absolute -top-6 left-6 w-28 text-beige/20" />
          <OrnamentHeart className="absolute bottom-2 right-8 w-24 text-beige/20" />
          <Reveal>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] max-w-3xl mx-auto">{t('home.finalTitle')}</h2>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <PillLink to={p('contact')} variant="sage" testId="final-cta-contact">{t('home.finalCta')} <ArrowRight size={20} /></PillLink>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
